import type { ChatMessages, Message } from "@/reducers/messagesSlice"
import type { SearchHit } from "./searchResultParser"
import MessageHelpers from "./messageHelpers"
import {
  archiveTimestampMicroseconds,
  isSearchHistoryAnchor,
} from "./searchHistoryAnchor"

type Anchor =
  | { messageId: string; missingOriginalId?: never }
  | { messageId?: never; missingOriginalId: string }

export function resolveSearchHistoryAnchor(
  chat: ChatMessages,
  hit: Pick<SearchHit["document"], "id" | "timestamp">,
): Anchor | undefined {
  const entries = [...chat.messages, ...chat.messageCorrections]
  let entry = entries.find((message) => isSearchHistoryAnchor(message, hit))
  const seen = new Set<string>()
  while (entry?.replaceId) {
    if (seen.has(entry.id))
      throw new Error("Circular archive correction reference")
    seen.add(entry.id)
    const replaceId = entry.replaceId
    const original = entries.find(
      (message) => message.id === replaceId || message.originId === replaceId,
    )
    if (!original) return { missingOriginalId: entry.replaceId }
    entry = original
  }
  return entry ? { messageId: entry.id } : undefined
}

export function searchHistoryMessages(chat: ChatMessages): Message[] {
  const correctionsByTarget = new Map<string, Message[]>()
  for (const correction of chat.messageCorrections) {
    if (!correction.replaceId) continue
    const corrections = correctionsByTarget.get(correction.replaceId) ?? []
    corrections.push(correction)
    correctionsByTarget.set(correction.replaceId, corrections)
  }
  return chat.messages.map((message) => {
    const targets = [
      message.id,
      ...(message.originId ? [message.originId] : []),
    ]
    const visited = new Set<string>()
    let latest: Message | undefined
    while (targets.length) {
      const target = targets.pop()
      if (!target) continue
      for (const correction of correctionsByTarget.get(target) ?? []) {
        if (visited.has(correction.id)) continue
        visited.add(correction.id)
        if (
          !latest ||
          archiveTimestampMicroseconds(correction.timestamp) >
            archiveTimestampMicroseconds(latest.timestamp)
        )
          latest = correction
        targets.push(correction.id)
        if (correction.originId) targets.push(correction.originId)
      }
    }
    const corrected = latest
      ? MessageHelpers.applyCorrection(message, latest)
      : message
    return chat.messageRetractions[message.id]
      ? MessageHelpers.markRetracted(corrected)
      : corrected
  })
}
